import React from "react";
import { Plus, Trash2 } from "lucide-react";

function PalletGroupRow({ idx, group, canRemove, onUpdate, onRemove }) {
    const subtotal =
        (Number(group.pallets) || 0) * (Number(group.units_per_pallet) || 0);
    return (
        <div
            className="grid grid-cols-12 gap-2 items-center"
            data-testid={`lt-group-${idx}`}
        >
            <span className="col-span-2 text-xs text-gray-600">Pallets</span>
            <input
                type="number"
                min="1"
                value={group.pallets}
                onChange={(e) => onUpdate({ pallets: e.target.value })}
                className="col-span-2 odr-input rounded-sm border border-gray-300 px-2 text-sm text-right odr-mono"
            />
            <span className="col-span-1 text-center text-gray-500">@</span>
            <input
                type="number"
                min="1"
                value={group.units_per_pallet}
                onChange={(e) => onUpdate({ units_per_pallet: e.target.value })}
                className="col-span-2 odr-input rounded-sm border border-gray-300 px-2 text-sm text-right odr-mono"
                placeholder="units"
            />
            <span className="col-span-1 text-xs text-gray-600 text-center">
                × copies
            </span>
            <input
                type="number"
                min="1"
                value={group.copies}
                onChange={(e) => onUpdate({ copies: e.target.value })}
                className="col-span-2 odr-input rounded-sm border border-gray-300 px-2 text-sm text-right odr-mono"
            />
            <span className="col-span-1 text-right odr-mono text-xs text-gray-500">
                ={subtotal}
            </span>
            <button
                type="button"
                onClick={onRemove}
                disabled={!canRemove}
                className="col-span-1 text-gray-500 hover:text-red-700 disabled:opacity-30"
            >
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
}

export default function ManualPane({
    activeLines,
    orderLineId,
    onOrderLineChange,
    productionDate,
    onProductionDateChange,
    palletGroups,
    onAddGroup,
    onUpdateGroup,
    onRemoveGroup,
    palletPreview,
}) {
    return (
        <div className="space-y-3" data-testid="lt-manual-pane">
            <p className="text-sm text-gray-600">
                Advance / manual generation — no production records will be created.
                Useful for printing tags ahead of production.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Order Line / Product
                    </label>
                    <select
                        value={orderLineId}
                        onChange={(e) => onOrderLineChange(e.target.value)}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                        data-testid="lt-manual-line"
                    >
                        {activeLines.map((l) => (
                            <option key={l.id} value={l.id}>
                                L{l.line_no} · {l.product_snapshot?.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Production Date
                    </label>
                    <input
                        type="date"
                        value={productionDate}
                        onChange={(e) => onProductionDateChange(e.target.value)}
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                    />
                </div>
            </div>
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                        Pallet Groups
                    </span>
                    <button
                        type="button"
                        onClick={onAddGroup}
                        className="text-sm text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                        data-testid="lt-add-group"
                    >
                        <Plus className="w-4 h-4" /> Add group
                    </button>
                </div>
                <div className="space-y-2">
                    {palletGroups.map((g, idx) => (
                        <PalletGroupRow
                            key={g._uid || idx}
                            idx={idx}
                            group={g}
                            canRemove={palletGroups.length > 1}
                            onUpdate={(patch) => onUpdateGroup(idx, patch)}
                            onRemove={() => onRemoveGroup(idx)}
                        />
                    ))}
                </div>
                <div className="mt-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                    Will generate{" "}
                    <span className="font-semibold odr-mono">
                        {palletPreview.pages}
                    </span>{" "}
                    page(s) covering{" "}
                    <span className="odr-mono">
                        {palletPreview.units.toLocaleString()}
                    </span>{" "}
                    unit(s).
                </div>
            </div>
        </div>
    );
}
